/**
 * UI-Modus (Komplexitäts-Achse) — orthogonal zum Bereich (`area.ts`):
 *
 *   • 'schlicht'  — die einzige App-Ansicht für Fachkräfte.
 *   • 'voll'      — die volle Programm-Ansicht, nur noch für `admin`.
 *
 * Es gibt bewusst kein Umschalten mehr zwischen zwei Dashboard-/App-Welten:
 * klinik und ambulant sind fest auf 'schlicht' verdrahtet (eine Software,
 * ein Layout). `admin` bleibt 'voll' (dichtere Verwaltungsoberfläche),
 * `patient` bleibt 'schlicht' — beide unverändert gegenüber vorher.
 *
 * Reine Funktionen (kein React) — der `ModeContext` baut darauf auf.
 */

import type { Area } from './area';

export type UiMode = 'schlicht' | 'voll';

/** Pro-Gerät-Präferenz im localStorage. Nur noch für Altdaten relevant. */
export const UI_MODE_STORAGE_KEY = 'nutriflow:ui-mode';

/** Kein Bereich darf mehr umschalten — es gibt nur noch eine Ansicht pro Bereich. */
export function canToggleMode(_area: Area): boolean {
  return false;
}

/**
 * Der erzwungene Modus eines Bereichs, oder `null` wenn der Bereich frei
 * umschaltbar ist.
 */
export function lockedModeForArea(area: Area): UiMode | null {
  if (area === 'patient') return 'schlicht';
  if (area === 'admin') return 'voll';
  if (area === 'klinik' || area === 'ambulant') return 'schlicht';
  return null;
}

/**
 * Effektiver Modus: jeder Bereich hat jetzt genau einen fest zugeordneten
 * Modus (siehe `lockedModeForArea`) — die frühere gespeicherte Präferenz
 * (`stored`) wird nicht mehr berücksichtigt.
 */
export function resolveMode(area: Area, _stored: UiMode | null): UiMode {
  return lockedModeForArea(area) ?? 'schlicht';
}

/** Liest die gespeicherte Präferenz; `null` wenn keine/ungültig. */
export function readStoredUiMode(): UiMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(UI_MODE_STORAGE_KEY);
    return raw === 'voll' || raw === 'schlicht' ? raw : null;
  } catch {
    return null;
  }
}

/** Schreibt die Präferenz; schluckt Fehler (z. B. Privatmodus). */
export function writeStoredUiMode(mode: UiMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(UI_MODE_STORAGE_KEY, mode);
  } catch {
    /* localStorage kann blockiert sein — Präferenz dann nur für die Session */
  }
}
